import React, { useCallback, useEffect, useState } from 'react';
import logError from 'App/babel/helpers/logError';
import fieldSort from '../../../../babel/helpers/fieldSort';
import { Button, Modal, ModalContent, ModalHeader } from '@upsales/components';
import { ModalControls } from '@upsales/components';
import T from 'Components/Helpers/translate';
import { ModalProps, useModalClose } from 'App/components/Modals/Modals';
import LabeledInput from 'App/components/Inputs/LabeledInput';
import Tooltip from '@upsales/components/Tooltip/Tooltip';
import { asyncModalAdapter } from 'App/helpers/angularPortingHelpers';
import getAngularModule from 'App/babel/angularHelpers/getAngularModule';
import _ from 'lodash';
import { useSelector } from 'App/components/hooks';
import { DocumentTemplate } from 'App/resources/AllIWant';
import { makeCancelable } from 'Helpers/promise';
import ClientSelect from 'App/components/ClientSelect/ClientSelect';
import NotesWithSignature from 'Components/Inputs/NotesWithSignature/NotesWithSignature';
import ContactSelect from 'App/components/ContactSelect/ContactSelect';
import BemClass from '@upsales/components/Utils/bemClass';
import { UserDefinedObject } from 'App/resources/UserDefinedObject';
import openModal from 'App/services/Modal/Modal';
import documentResource from 'Resources/document';

import './EditUDO.scss';

// Reimplementing dropdown functionality from upsales/common/directives/index.js
// Keeping local because it's hacky and specifically made to work here. Can be moved to hooks folder
// if it turns out to be generically useful
const useDropdownToggle = () => {
	const [dropdownOpen, setDropdownOpen] = useState(false);

	const closeDropdownEvent = useCallback((event: { preventDefault: Function }) => {
		event.preventDefault();
		setDropdownOpen(false);
		document.removeEventListener('mouseup', closeDropdownEvent);
	}, []);

	const closeDropdown = function () {
		closeDropdownEvent({ preventDefault: () => {} });
	};

	const openDropdown = function () {
		setDropdownOpen(true);
		document.addEventListener('mouseup', closeDropdownEvent);
	};

	const toggleDropdown = dropdownOpen ? closeDropdown : openDropdown;

	useEffect(
		() => () => {
			document.removeEventListener('mouseup', closeDropdownEvent);
		},
		[]
	);

	return { dropdownOpen, toggleDropdown };
};

type Props = {
	typeId: `${1 | 2 | 3 | 4}`;
	id: number | string;
	object: any;
} & ModalProps<UserDefinedObject>;

const EditUDOModal = ({ modalId, typeId, id, ...props }: Props) => {
	const classes = new BemClass('EditUDOModal', props.className);

	const [saving, setSaving] = useState(false);

	const [changingAccount, setChangingAccount] = useState(false);
	const [changingContact, setChangingContact] = useState(false);
	const [initialContact, setInitialContact] = useState(null);
	const [initialClient, setInitialClient] = useState(null);

	const [loading, setLoading] = useState(true);
	const [initialObjectJson, setInitialObjectJson] = useState('');

	const [object, setObject] = useState<Record<string, any>>({ ...(props.object ?? {}) });
	const [hasNoLink, setHasNoLink] = useState(true);

	const UserDefinedObject = getAngularModule('UserDefinedObject') as any;

	const documentTemplates = useSelector(({ App }) => App.documentTemplates[`object${typeId}`]);
	const metadata = useSelector(({ App }) => App.metadata);

	const notes = { title: T('default.notes'), model: 'notes1' };

	const { dropdownOpen: showDocumentsDropdown, toggleDropdown } = useDropdownToggle();

	const changeContact = function () {
		const finalValue = !changingContact;
		setChangingContact(finalValue);

		if (finalValue) {
			setInitialContact(object.contact);
		} else {
			setObject({
				...object,
				contact: initialContact
			});
		}
	};

	const changeAccount = function () {
		const finalValue = !changingAccount;
		setChangingAccount(finalValue);

		if (finalValue) {
			setInitialClient(object.client);
			setInitialContact(object.contact);
		} else {
			setObject({
				...object,
				client: initialClient,
				contact: initialContact
			});
		}
	};

	const createDocument = function (template: DocumentTemplate) {
		var params = {
			entityId: object.id,
			templateId: template.id,
			type: template.type,
			name: template.name
		};

		if (Tools.FeatureHelper.hasSoftDeployAccess('PREVIEW_PDF_REACT')) {
			openModal('PreviewPdfModal', {
				isUploadedTemplate: true,
				documentResource: {
					resource: documentResource,
					entityId: params.entityId,
					templateId: params.templateId,
					type: params.type,
					documentName: params.name
				}
			});
		} else {
			Tools.$upModal.open('pdfPreview', params);
		}
	};

	useEffect(() => {
		const getObject = async () => {
			if (id) {
				return UserDefinedObject.setId(typeId).get(id);
			} else {
				var newObject = UserDefinedObject.setId(typeId).new();
				if (props.object) {
					newObject = _.merge(newObject, props.object);
				}
				return { data: newObject };
			}
		};

		const objectPromise = makeCancelable(getObject());
		objectPromise.promise
			.then(function (res: any) {
				if (res.data) {
					res.data.custom.sort(fieldSort);
				}

				const finalObject = { ...object, ...res.data };

				setObject(finalObject);
				setHasNoLink(!finalObject.client && !finalObject.contact && !finalObject.project);
				setInitialObjectJson(JSON.stringify(finalObject));
			})
			.catch(err => logError(err, 'Failed to fetch user defined object'))
			.finally(() => setLoading(false));

		return () => objectPromise.cancel();
	}, []);

	useModalClose(
		modalId,
		e => {
			let currentJson;
			try {
				currentJson = JSON.stringify(object);
			} catch {
				props.close(undefined, true);
			}

			if (currentJson !== initialObjectJson) {
				e.preventDefault();

				openModal('Alert', {
					title: 'default.abort',
					body: 'confirm.abortEdit',
					confirmButtonText: 'default.abortEdit',
					cancelButtonText: 'default.returnToEdit',
					onClose: async confirmed => {
						if (!confirmed) {
							return;
						}
						props.close(undefined, true);
					}
				});
			} else {
				props.close(undefined, true);
			}
		},
		[object, initialObjectJson]
	);

	const save = function () {
		setSaving(true);
		UserDefinedObject.setId(typeId)
			.save(object)
			.then(function (res: { data: UserDefinedObject }) {
				props.close(res.data, true);
			})
			.finally(function () {
				setSaving(false);
			})
			.catch((err: Error) => {
				logError(err, 'Failed to save user defined object');
			});
	};

	const udo = _.find(metadata?.params.UserDefinedObject!, {
		id: parseInt(typeId)
	})!;

	const hasContactLink = udo.link === 6;
	const hasAccountLink = udo.link === 4;
	const hasProjectLink = udo.link === 1;

	const contactRequired = hasContactLink;
	const accountRequired = hasContactLink || hasAccountLink;
	const projectRequired = hasProjectLink;

	const hasDocumentTemplates = !!documentTemplates?.length;

	if (!typeId) {
		logError(new Error('No typeId provided'), 'EditUDO modal requires a typeId');
		props.close(undefined, true);
		return null;
	}

	if (loading) {
		return null;
	}

	const requiredMissing: string[] = [];
	if (projectRequired && !object.project) {
		requiredMissing.push(T('validation.missingRequiredFields', { field: T('default.project') }));
	}
	if (accountRequired && !object.client) {
		requiredMissing.push(T('validation.missingRequiredFields', { field: T('default.account') }));
	}
	if (contactRequired && !object.contact) {
		requiredMissing.push(T('validation.missingRequiredFields', { field: T('default.contact') }));
	}

	const disabledSave = !!requiredMissing.length;

	const contactSelect = (
		<ContactSelect
			clientId={object.client?.id}
			placeholder={`${T('default.select')} ${T('default.contact').toLowerCase()}`}
			disabled={!object.client}
			onChange={({ target }) => {
				setObject(prev => ({
					...prev,
					contact: target?.added ?? null
				}));
			}}
			value={object.contact}
			required={contactRequired}
			onlyActive
		/>
	);

	const clientSelect = (
		<ClientSelect
			value={object.client}
			onChange={({ target }) => {
				setObject(prev => ({
					...prev,
					client: target?.added ?? null,
					contact: null
				}));
			}}
			required={accountRequired}
			onlyActive
		/>
	);

	return (
		<Modal className={classes.b()} size="lg">
			<form
				autoComplete="off"
				name="UserDefinedObject"
				onSubmit={event => {
					event.preventDefault();
					save();
				}}
			>
				<ModalHeader
					title={`${T(object.id ? 'default.edit' : 'default.create')} ${udo.name.toLowerCase()}`}
					icon="tag"
					onClose={props.close}
				/>
				<ModalContent>
					<div className="up-panel">
						<div className="up-panel-content">
							{/** Linked to select2 (account, contact, campaign) */}
							<div className="row">
								{hasNoLink && (
									<div>
										{/** ACCOUNT */}
										{accountRequired && (
											<div className="col-md-6">
												<LabeledInput title={T('default.account')} required>
													{clientSelect}
												</LabeledInput>
											</div>
										)}

										{/** CONTACT */}
										{contactRequired && (
											<div className="col-md-6">
												<LabeledInput title={T('default.contact')} required>
													{contactSelect}
												</LabeledInput>
											</div>
										)}

										{/** PROJECT */}
										{projectRequired && (
											<div className="col-md-6">
												<LabeledInput title={T('default.project')} required>
													<ReactTemplates.INPUTS.upCampaigns
														name="project"
														value={object.project}
														onChange={val => {
															setObject(prev => ({ ...prev, project: val }));
														}}
														required={projectRequired}
														className="form-control"
													/>
												</LabeledInput>
											</div>
										)}
									</div>
								)}

								{/** EDIT MODE */}
								{!hasNoLink && (
									<div>
										{/** ACCOUNT */}
										{accountRequired && (
											<div className="col-md-6">
												{/** Display the current account info */}
												{!changingAccount && (
													<LabeledInput title={T('default.account')}>
														<div>
															{object.client && (
																<p data-test-id="udo-account-text">
																	{object.client.name}
																</p>
															)}
															{/** Edit button */}
															{!changingContact && (
																<Tooltip
																	title={
																		T('change') + ' ' + T('account').toLowerCase()
																	}
																	position="left"
																	style={{
																		position: 'absolute',
																		right: '-2px',
																		top: '12px'
																	}}
																>
																	<Button
																		type="link"
																		onClick={changeAccount}
																		icon="edit"
																	/>
																</Tooltip>
															)}
														</div>
													</LabeledInput>
												)}

												{/** Edit the current account info */}
												{changingAccount && (
													<div id="select-account-section" className="form-group no-margin">
														<LabeledInput
															title={T('default.account')}
															required
															topRight={
																!!object.id && (
																	<Button
																		size="xs"
																		type="link"
																		onClick={changeAccount}
																	>
																		{T('default.abort')}
																	</Button>
																)
															}
														>
															{clientSelect}
														</LabeledInput>
													</div>
												)}
											</div>
										)}

										{/** CONTACT */}
										{contactRequired && (
											<div className="col-md-6">
												{/**  Display the contact */}
												{!changingAccount && !changingContact && (
													<LabeledInput title={T('default.contact')}>
														<div>
															{object.contact && <span>{object.contact.name}</span>}
															{/** Edit button */}
															<Tooltip
																title={T('change') + ' ' + T('contact').toLowerCase()}
																position="left"
																style={{
																	position: 'absolute',
																	right: '-2px',
																	top: '12px'
																}}
															>
																<Button
																	type="link"
																	onClick={changeContact}
																	icon="edit"
																/>
															</Tooltip>
														</div>
													</LabeledInput>
												)}

												{/** Select the contact if button is pressed */}
												{(changingAccount || changingContact) && (
													<div id="select-contact-section" className="form-group no-margin">
														<LabeledInput
															title={T('default.contact')}
															required={contactRequired}
															topRight={
																/** Cancel account change button */
																changingContact && object.id ? (
																	<span>
																		<Button
																			size="xs"
																			type="link"
																			onClick={changeContact}
																		>
																			{T('default.abort')}
																		</Button>
																	</span>
																) : null
															}
														>
															{contactSelect}
														</LabeledInput>
													</div>
												)}
											</div>
										)}

										{/** PROJECT */}
										{projectRequired && (
											<div className="col-md-6">
												<LabeledInput title={T('default.project')}>
													{object.project && <p>{object.project.name}</p>}
												</LabeledInput>
											</div>
										)}
									</div>
								)}
							</div>
						</div>

						{/** CUSTOMFIELDS */}
						<div className={classes.elem('customFields').add('row').b()}>
							{object.custom?.map((field: any) => {
								return (
									field.$hasAccess &&
									(field.editable || field.visible) && (
										<div className="col-md-6" style={{ minHeight: '80px' }}>
											<LabeledInput
												title={field.name}
												required={!!(field.obligatoryField && field.editable)}
											>
												<div className="input">
													<ReactTemplates.customFieldInput
														field={field}
														valueChange={(value: any) => {
															setObject(prev => ({
																...prev,
																custom: prev.custom.map((currentField: any) => {
																	if (field.id === currentField.id) {
																		return { ...currentField, value };
																	}
																	return currentField;
																})
															}));
														}}
														disabled={saving}
														entity={'udo'}
														usenewdate
														useNewTime
														useNumberInput
														ngModel={field.value}
													/>
												</div>
											</LabeledInput>
										</div>
									)
								);
							})}
						</div>

						<NotesWithSignature
							title={notes.title}
							onChange={(val: string) => {
								setObject(prev => ({ ...prev, [notes.model]: val }));
							}}
							value={object[notes.model]}
						/>
					</div>
				</ModalContent>

				<ModalControls>
					{/** DOCUMENTS */}
					{hasDocumentTemplates && object.id && (
						<div className={`dropdown dropup btn-group pull-left ${showDocumentsDropdown ? 'open' : ''}`}>
							<Button type="lined" onClick={toggleDropdown}>
								<b className="fa fa-file-o"></b> {T('default.create')}{' '}
								{T('default.document').toLowerCase()} <b className="fa fa-caret-up"></b>
							</Button>

							<ul className="dropdown-menu up-dropdown-white limited">
								{documentTemplates.map((template: any) => (
									<li key={template.id}>
										<a onClick={() => createDocument(template)}>{template.name}</a>
									</li>
								))}
							</ul>
						</div>
					)}

					{/** SAVE */}
					{object.userEditable && (
						<Tooltip title={requiredMissing.join(', ')} disabled={!disabledSave} distance={20}>
							<button
								className={'btn btn-sm up-btn btn-green no-shadow'}
								type="submit"
								disabled={saving || disabledSave}
							>
								{T('default.save')}
							</button>
						</Tooltip>
					)}
					<Button type="link" onClick={() => props.close()}>
						{T('default.abort')}
					</Button>
				</ModalControls>
			</form>
		</Modal>
	);
};

export const openEditUDOModal = asyncModalAdapter({
	upModalName: 'editUserDefinedObject',
	openModalName: 'EditUDO',
	featureFlag: 'REACT_EDIT_USER_DEFINED_OBJECT'
});

export default EditUDOModal;
